import React from "react";
import { Badge, Section } from "@cinesys/ui/components";
import styled from "styled-components";

/**
 * Manager-side Spotter usage section (Monday 12650205737, round 4 — redesign per Addison: drop
 * Recent Activity from this admin view entirely (the customer-facing screen already has that
 * detail if it's ever needed here too), make overage $ the headline stat instead of one row among
 * equals, and let the section carry its own visual identity rather than reusing the generic
 * KeyValue-list layout every other admin screen uses. Structure: a season-total overage hero,
 * then one quota-gauge card per category — this is the shape an admin actually scans this page
 * for ("does anyone owe money, and by how much"), not a full data dump.
 */

const Eyebrow = styled.div`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--gray100);
`;

const HeroWrap = styled.div`
  padding: 20px 20px 18px;
  border-bottom: 1px solid var(--dark300);
`;

const HeroFigure = styled.div<{ $over: boolean }>`
  font-size: 42px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
  line-height: 1.1;
  margin-top: 6px;
  color: ${(p) => (p.$over ? "var(--red400)" : "var(--green400)")};
`;

const HeroCaption = styled.div`
  margin-top: 8px;
  font-size: 14px;
  color: var(--gray200);

  strong {
    font-variant-numeric: tabular-nums;
    color: var(--gray400);
  }
`;

const Categories = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  padding: 18px 20px 20px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const CategoryCard = styled.div`
  background-color: var(--dark110);
  border: 1px solid var(--dark300);
  border-radius: 10px;
  padding: 16px;
`;

const CategoryHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const CategoryName = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: var(--gray400);
`;

const GaugeTrack = styled.div`
  height: 8px;
  border-radius: 4px;
  background-color: var(--dark300);
  overflow: hidden;
`;

const GaugeFill = styled.div<{ $pct: number; $tone: string }>`
  height: 100%;
  width: ${(p) => Math.min(Math.max(p.$pct, 0), 100)}%;
  background-color: ${(p) => `var(--${p.$tone}400)`};
  border-radius: 4px;
`;

const GaugeLabel = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
  font-size: 13px;
  color: var(--gray200);
  font-variant-numeric: tabular-nums;
`;

const OverageRow = styled.div<{ $over: boolean }>`
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid var(--dark300);
`;

const OverageFigure = styled.span<{ $over: boolean }>`
  font-size: 22px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$over ? "var(--red400)" : "var(--gray300)")};
`;

const OverageLabel = styled.span`
  font-size: 13px;
  color: var(--gray100);
`;

const Caption = styled.div`
  margin-top: 10px;
  font-size: 12px;
  color: var(--gray100);
`;

const NotEntitled = styled.div`
  padding: 18px 0 6px;
  text-align: center;
  color: var(--gray100);
  font-size: 13px;
`;

function formatDollars(amount: number): string {
  return `$${amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatHours(hours: number): string {
  return `${hours.toFixed(1)}h`;
}

/** green under 80% of quota, amber approaching it, red once over — same thresholds the Usage
 *  Notifier (n8n) already alerts a customer at (80/90/100), so the color story matches the
 *  emails they'd already be getting. */
function gaugeTone(pct: number): string {
  if (pct >= 100) {
    return "red";
  }
  if (pct >= 80) {
    return "orange";
  }
  return "green";
}

function CategoryGauge(props: {
  title: string;
  summary: SpotterUsageCategorySummary;
}) {
  const { title, summary } = props;
  const over = summary.overage_cost > 0;

  return (
    <CategoryCard>
      <CategoryHead>
        <CategoryName>{title}</CategoryName>
        <Badge
          color={summary.entitled ? "var(--green400)" : "var(--gray200)"}
          label={summary.entitled ? "Entitled" : "Not entitled"}
        />
      </CategoryHead>

      {!summary.entitled ? (
        <NotEntitled>Not entitled this season.</NotEntitled>
      ) : (
        <>
          <GaugeTrack>
            <GaugeFill
              $pct={summary.pct_used}
              $tone={gaugeTone(summary.pct_used)}
            />
          </GaugeTrack>
          <GaugeLabel>
            <span>
              {formatHours(summary.hours_used)} of{" "}
              {formatHours(summary.quota_hours)}
            </span>
            <span>{summary.pct_used.toFixed(0)}%</span>
          </GaugeLabel>

          <OverageRow $over={over}>
            <OverageFigure $over={over}>
              {formatDollars(summary.overage_cost)}
            </OverageFigure>
            <OverageLabel>
              overage{" "}
              {summary.overage_hours > 0 &&
                `(${formatHours(summary.overage_hours)} over)`}
            </OverageLabel>
          </OverageRow>

          <Caption>
            {formatDollars(summary.overage_rate)}/hr over quota
            {summary.period_start && summary.period_end
              ? ` · ${summary.period_start} – ${summary.period_end}`
              : " · season not configured"}
          </Caption>
        </>
      )}
    </CategoryCard>
  );
}

const SpotterUsageSection = (props: { section: SpotterUsageSection }) => {
  const { section } = props;
  const totalOverage =
    section.practice.overage_cost + section.game.overage_cost;
  const over = totalOverage > 0;
  const anyEntitled = section.practice.entitled || section.game.entitled;

  return (
    <Section noMargin>
      <HeroWrap>
        <Eyebrow>Spotter · Overage this season</Eyebrow>
        <HeroFigure $over={over}>{formatDollars(totalOverage)}</HeroFigure>
        <HeroCaption>
          {over ? (
            <>
              <strong>{formatDollars(section.practice.overage_cost)}</strong>{" "}
              practice +{" "}
              <strong>{formatDollars(section.game.overage_cost)}</strong> game
            </>
          ) : anyEntitled ? (
            "Both categories are within quota."
          ) : (
            "No entitled Spotter categories configured."
          )}
        </HeroCaption>
      </HeroWrap>
      <Categories>
        <CategoryGauge title="Practice" summary={section.practice} />
        <CategoryGauge title="Game" summary={section.game} />
      </Categories>
    </Section>
  );
};

export default SpotterUsageSection;
